import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Hook to restore scroll position when navigating between pages
 * Uses browser history state to persist scroll positions
 *
 * Features:
 * - Saves scroll position to history state before navigation
 * - Restores scroll position from history state after navigation
 * - Supports scrollTo targets via data-scroll-to attributes
 * - Defaults to scrollTop = 0 when no scrollTo and no saved position exists
 * - Skips scroll handling for image close transitions (layout.js handles these with fade animations)
 */

const isViewerPath = (pathname) => /\.(?:img|vid|txt)$/.test(pathname || '');

export function useScrollRestoration() {
    const location = useLocation();
    const navigate = useNavigate();
    const prevPathnameRef = useRef(location.pathname);
    const scrollYRef = useRef(0);

    useEffect(() => {
        const trackScroll = () => {
            scrollYRef.current = window.scrollY;
        };

        scrollYRef.current = window.scrollY;

        window.addEventListener('scroll', trackScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', trackScroll);
        };
    }, []);

    const locationRef = useRef({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state,
    });
    locationRef.current = {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state,
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                const scrollY = scrollYRef.current;
                const loc = locationRef.current;
                if (!isViewerPath(loc.pathname)) {
                    const currentState = loc.state || {};
                    navigate(loc.pathname + loc.search + loc.hash, {
                        replace: true,
                        state: {
                            ...currentState,
                            scrollPosition: scrollY,
                        },
                    });
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);

            const scrollY = scrollYRef.current;
            const loc = locationRef.current;
            if (!isViewerPath(loc.pathname) && scrollY > 0) {
                const currentState = loc.state || {};
                navigate(loc.pathname + loc.search + loc.hash, {
                    replace: true,
                    state: {
                        ...currentState,
                        scrollPosition: scrollY,
                    },
                });
            }
        };
    }, [navigate]);

    useLayoutEffect(() => {
        const state = location.state || {};
        const prevPathname = prevPathnameRef.current;
        prevPathnameRef.current = location.pathname;

        // Media viewers are fixed overlays; a masonry page kept underneath must
        // stay scrolled where the visitor left it.
        if (isViewerPath(location.pathname)) {
            return;
        }

        const isClosingImage = isViewerPath(prevPathname) && !isViewerPath(location.pathname);
        if (isClosingImage) {
            return;
        }

        if (typeof state.scrollPosition === 'number') {
            const scrollY = state.scrollPosition;
            window.scrollTo(0, scrollY);
            return;
        }

        if (typeof state.scrollTo === 'string' && state.scrollTo) {
            const targetElement = document.querySelector(`[data-scroll-to="${state.scrollTo}"]`);

            if (targetElement) {
                const articleElement = document.querySelector('article[data-page]');
                const hasAnimation = articleElement && (
                    articleElement.classList.contains('translate-y-4') ||
                    (articleElement.style.animation && articleElement.style.animation.includes('slideUpFadeIn'))
                );
                const animationOffset = hasAnimation ? -16 : 0;

                const rect = targetElement.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const scrollTop = window.scrollY || document.documentElement.scrollTop;
                const elementTop = rect.top + scrollTop;
                const elementCenter = elementTop + rect.height / 2;
                const targetScroll = elementCenter - viewportHeight / 2 + animationOffset;

                window.scrollTo(0, Math.max(0, targetScroll));
                return;
            }
        }

        window.scrollTo(0, 0);
    }, [location.pathname, location.key]);
}

export default useScrollRestoration;
